import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isBusinessRegistrationBlockedRole } from '../../utils/businessRegistrationAccess';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isConsumer = user?.role === 'consumer';
  const isBusinessOwner = user?.role === 'business_owner';
  const isBlockedFromBusinessRegistration = isBusinessRegistrationBlockedRole(user?.role);
  const canShowBusinessRegistrationLink = !isAuthenticated || !isBlockedFromBusinessRegistration;

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
    navigate('/login');
  };

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Categories', to: '/categories' },
    { label: 'Businesses', to: '/businesses' },
  ];

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[62px]">

          {/* ── Logo ── */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0 group">
            <div className="w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center shadow-sm group-hover:bg-indigo-700 transition-colors duration-200">
              <span className="text-white font-black text-[14px] tracking-tight">VMC</span>
            </div>
            <div className="leading-none">
              <span className="text-[15px] font-bold text-slate-900 tracking-tight">VMC Reviews</span>
              <p className="text-[9.5px] text-slate-400 font-medium tracking-wider uppercase leading-none mt-0.5 mb-0.5">Business Platform</p>
            </div>
          </Link>

          {/* ── Right Actions ── */}
          <div className="hidden md:flex items-center gap-4 ml-auto">
            {/* Primary Nav Links */}
            <div className="flex items-center gap-0.5">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative no-underline px-4 py-2 text-[13.5px] font-medium rounded-lg transition-all duration-150 ${isActive(link.to)
                      ? 'text-indigo-600 bg-indigo-50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                >
                  {link.label}
                  {isActive(link.to) && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-indigo-500 rounded-full" />
                  )}
                </Link>
              ))}
            </div>

            {/* List Your Business — only for non-admin, non-business-owner users */}
            {canShowBusinessRegistrationLink && (
              <Link
                to="/register-business"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 active:scale-95 transition-all duration-150 shadow-sm hover:shadow-indigo-200 hover:shadow-md"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                </svg>
                Register Business
              </Link>
            )}

            {isAuthenticated ? (
              <>
                {/* Divider */}
                <div className="w-px h-5 bg-slate-200 mx-1" />

                {isAdmin ? (
                  /* ── Admin: Dashboard link + avatar ── */
                  <>
                    <Link
                      to="/admin/dashboard"
                      className="inline-flex items-center gap-2 px-3.5 py-1.5 text-[13px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200/80 rounded-lg hover:bg-indigo-100 hover:border-indigo-300 transition-all duration-150"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      Dashboard
                    </Link>
                    <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-full flex items-center justify-center shadow-sm ring-2 ring-white">
                      <span className="text-white text-[11px] font-bold">
                        {user?.name?.charAt(0).toUpperCase() ?? 'A'}
                      </span>
                    </div>
                  </>
                ) : isBusinessOwner ? (
                  /* ── Business Owner: Dashboard link + avatar ── */
                  <>
                    <Link
                      to="/business/dashboard"
                      className="inline-flex items-center gap-2 px-3.5 py-1.5 text-[13px] font-semibold text-orange-700 bg-orange-50 border border-orange-200/80 rounded-lg hover:bg-orange-100 hover:border-orange-300 transition-all duration-150"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      Dashboard
                    </Link>
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center shadow-sm ring-2 ring-white">
                      <span className="text-white text-[11px] font-bold">
                        {user?.name?.charAt(0).toUpperCase() ?? 'B'}
                      </span>
                    </div>
                  </>
                ) : (
                  /* ── Consumer: avatar + name — clickable ── */
                  <button
                    onClick={() => navigate('/consumer/dashboard')}
                    className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-slate-100 transition-all duration-150 cursor-pointer"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-sm ring-2 ring-white">
                      <span className="text-white text-[11px] font-bold">
                        {user?.name?.charAt(0).toUpperCase() ?? 'U'}
                      </span>
                    </div>
                    <span className="text-[13px] font-medium text-slate-700 max-w-[120px] truncate hover:text-slate-900">
                      {user?.name}
                    </span>
                  </button>
                )}

                <button
                  onClick={handleLogout}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-150"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </>
            ) : (
              /* ── Logged out: Sign In + Sign Up ── */
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 text-[13px] border font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-all duration-150"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 text-[13px] font-semibold text-white bg-slate-900 rounded-lg hover:bg-slate-700 transition-all duration-150 shadow-sm"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* ── Mobile Hamburger ── */}
          <button
            className="md:hidden p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-all"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* ── Mobile Menu ── */}
      {mobileOpen && (
        <div className="md:hidden border-t border-slate-100 bg-white px-4 pt-3 pb-4 space-y-1 shadow-lg">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className={`block px-3 py-2.5 text-[13.5px] font-medium rounded-lg transition-all ${isActive(link.to)
                  ? 'text-indigo-600 bg-indigo-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
            >
              {link.label}
            </Link>
          ))}

          <div className="border-t border-slate-100 pt-3 mt-2 space-y-1.5">
            {isAuthenticated ? (
              <>
                {isAdmin ? (
                  <Link
                    to="/admin/dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 text-[13.5px] font-semibold text-indigo-600 bg-indigo-50 rounded-lg"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Dashboard
                  </Link>
                ) : isBusinessOwner ? (
                  <>
                    <Link
                      to="/business/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 text-[13.5px] font-semibold text-orange-700 bg-orange-50 rounded-lg"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      Dashboard
                    </Link>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        navigate('/consumer/dashboard');
                        setMobileOpen(false);
                      }}
                      className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-100 rounded-lg transition-all w-full"
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-[11px] font-bold">
                          {user?.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="text-[13px] font-medium text-slate-700 truncate">{user?.name}</span>
                    </button>
                  </>
                )}
                {canShowBusinessRegistrationLink && (
                  <Link
                    to="/register-business"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 text-[13.5px] font-semibold text-white bg-indigo-600 rounded-lg"
                  >
                    + Register Business
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[13.5px] font-medium text-red-600 hover:bg-red-50 rounded-lg transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 text-[13.5px] font-medium text-slate-600 hover:bg-slate-50 rounded-lg"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 text-[13.5px] font-semibold text-white bg-slate-900 rounded-lg text-center"
                >
                  Sign Up
                </Link>
                <Link
                  to="/register-business"
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2.5 text-[13.5px] font-semibold text-white bg-indigo-600 rounded-lg text-center"
                >
                  + Register Business
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
